import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import {
  type AdminMemberLifecycleAdjustmentItem,
  type AdminMemberLifecycleTransitionItem,
  useGetAdminMemberLifecycleQuery,
} from './api/memberLifecycleApi';

const PAGE_SIZE = 10;

interface MemberLifecycleSectionProps {
  personId: string;
}

function formatDate(value?: string) {
  if (!value) {
    return '—';
  }
  return format(new Date(value), 'MMM d, yyyy h:mm a');
}

function stateLabel(value?: string) {
  if (!value) {
    return '—';
  }
  return value.replaceAll('_', ' ');
}

function MetadataDetails({ metadata }: { metadata?: Record<string, unknown> }) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return <Typography color="text.secondary">—</Typography>;
  }

  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: 'pointer' }}>
        Metadata
      </Box>
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 1,
          fontSize: 12,
          mt: 1,
          overflow: 'auto',
          p: 1,
          whiteSpace: 'pre-wrap',
        }}
      >
        {JSON.stringify(metadata, null, 2)}
      </Box>
    </Box>
  );
}

function PaginationControls({
  hasMore,
  label,
  page,
  onPageChange,
}: {
  hasMore: boolean;
  label: string;
  page: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
      <Button
        size="small"
        variant="outlined"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Previous {label}
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={!hasMore}
        onClick={() => onPageChange(page + 1)}
      >
        Next {label}
      </Button>
    </Box>
  );
}

function TransitionHistoryTable({
  hasMore,
  items,
  page,
  onPageChange,
}: {
  hasMore: boolean;
  items: AdminMemberLifecycleTransitionItem[];
  page: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Transition History
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Transition</TableCell>
              <TableCell>Idempotency Key</TableCell>
              <TableCell>Metadata</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">No transition history.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={`${item.idempotencyKey}-${item.createdAt}`}>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                  <TableCell>{item.trigger}</TableCell>
                  <TableCell>
                    {stateLabel(item.fromState)} → {stateLabel(item.toState)} ({item.fromRank} →{' '}
                    {item.toRank})
                  </TableCell>
                  <TableCell>{item.idempotencyKey}</TableCell>
                  <TableCell>
                    <MetadataDetails metadata={item.metadata} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls
        label="transitions"
        page={page}
        hasMore={hasMore}
        onPageChange={onPageChange}
      />
    </Box>
  );
}

function AdminAdjustmentsTable({
  hasMore,
  items,
  page,
  onPageChange,
}: {
  hasMore: boolean;
  items: AdminMemberLifecycleAdjustmentItem[];
  page: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Admin Adjustments
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Created</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Overlay</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Metadata</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">No admin adjustments.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                  <TableCell>{item.adjustmentType}</TableCell>
                  <TableCell>
                    {stateLabel(item.previousState)} → {stateLabel(item.newState)}
                  </TableCell>
                  <TableCell>
                    {String(item.previousOverlayPaused ?? '—')} →{' '}
                    {String(item.newOverlayPaused ?? '—')}
                  </TableCell>
                  <TableCell>{item.reason}</TableCell>
                  <TableCell>
                    <MetadataDetails metadata={item.metadata} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls
        label="adjustments"
        page={page}
        hasMore={hasMore}
        onPageChange={onPageChange}
      />
    </Box>
  );
}

export default function MemberLifecycleSection({ personId }: MemberLifecycleSectionProps) {
  const [transitionPage, setTransitionPage] = useState(1);
  const [adjustmentPage, setAdjustmentPage] = useState(1);
  const { data, error, isFetching, isLoading, refetch } = useGetAdminMemberLifecycleQuery({
    personId,
    transitionPage,
    transitionPer: PAGE_SIZE,
    adjustmentPage,
    adjustmentPer: PAGE_SIZE,
  });

  return (
    <Box id="lifecycle" sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">Lifecycle</Typography>
        {(isLoading || isFetching) && <CircularProgress size={18} />}
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Refresh Lifecycle
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load lifecycle details. The rest of the member page is still available.
        </Alert>
      )}

      {isLoading && !data ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
        </Paper>
      ) : data ? (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 320px' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Current State
                </Typography>
                {data.state ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={stateLabel(data.state.state.name)}
                        color="primary"
                        size="small"
                      />
                      {data.state.state.terminal && (
                        <Chip label="Terminal" color="warning" size="small" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Rank {data.state.state.rank} • Version {data.state.version} • Sequence{' '}
                      {data.state.sequenceNumber}
                    </Typography>
                    {data.state.effectiveStatus && (
                      <Typography variant="body2" color="text.secondary">
                        Effective: {stateLabel(data.state.effectiveStatus.primary)}
                        {data.state.effectiveStatus.overlays?.length
                          ? ` + ${data.state.effectiveStatus.overlays.map(stateLabel).join(', ')}`
                          : ''}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No lifecycle state yet.</Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 320px' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Paused Overlay
                </Typography>
                {data.overlay ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box>
                      <Chip
                        label={data.overlay.paused ? 'Paused' : 'Not paused'}
                        color={data.overlay.paused ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Source: {data.overlay.pauseSource || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      HubSpot: {data.overlay.hubspotContactId || '—'} /{' '}
                      {data.overlay.hubspotCustomerLifecycleStage || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date unpaused: {formatDate(data.overlay.dateUnpaused)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last synced: {formatDate(data.overlay.lastSyncedAt)}
                    </Typography>
                  </Box>
                ) : (
                  <Typography color="text.secondary">No paused overlay.</Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          <TransitionHistoryTable
            items={data.transitions.items}
            page={transitionPage}
            hasMore={data.transitions.hasMore}
            onPageChange={setTransitionPage}
          />
          <AdminAdjustmentsTable
            items={data.adminAdjustments.items}
            page={adjustmentPage}
            hasMore={data.adminAdjustments.hasMore}
            onPageChange={setAdjustmentPage}
          />
        </>
      ) : null}
    </Box>
  );
}
